import { type FC, useCallback, useEffect, useMemo, useState } from "react";

import { useAdminUsers } from "../../hooks/useAdminUsers";
import { Alert } from "../common/Alert";
import { Badge } from "../common/Badge";
import { Button } from "../common/Button";
import { ConfirmationDialog } from "../common/ConfirmationDialog";
import { DataTable } from "../common/DataTable";
import { FormCheckbox } from "../common/forms/FormCheckbox";
import { FormInput } from "../common/forms/FormInput";
import { FormSelect } from "../common/forms/FormSelect";
import { LoadingState } from "../common/LoadingState";
import { MetricCard } from "../common/MetricCard";
import { Pagination } from "../common/Pagination";
import { PanelHeaderBlock } from "../common/PanelHeaderBlock";
import { PanelSection } from "../common/PanelSection";
import { Text } from "../common/Text";
import { AdminActiveFiltersRow } from "./AdminActiveFiltersRow";
import { AdminUserRow } from "./AdminUserRow";

const ROLE_FILTER_OPTIONS = [
  { label: "All Roles", value: "" },
  { label: "Admin", value: "admin" },
  { label: "User", value: "user" },
];

const PAGE_SIZE = 25;

const UserSessionSummary: FC<{
  filteredCount: number;
  totalCount: number;
  usersWithSessions: number;
  usersWithoutOrganizations: number;
}> = ({
  filteredCount,
  totalCount,
  usersWithSessions,
  usersWithoutOrganizations,
}) => (
  <div className="rounded-none border border-slate-300 bg-slate-50/80 p-4 dark:border-slate-700 dark:bg-arti-background">
    <div className="flex items-center justify-between gap-3">
      <Text variant="label">Session Controls</Text>
      <Badge variant="info" size="sm">
        {usersWithSessions} live
      </Badge>
    </div>

    <div className="mt-3 space-y-3">
      <Text as="p" className="leading-5">
        Invalidate active sessions directly from the directory. Filters help
        isolate privileged users, active users, and membership gaps before you
        take action.
      </Text>

      <div className="flex flex-wrap gap-2">
        <Badge variant="status" size="sm">
          {filteredCount} / {totalCount} visible
        </Badge>
        <Badge
          variant={usersWithoutOrganizations > 0 ? "warning" : "success"}
          size="sm"
        >
          {usersWithoutOrganizations} without orgs
        </Badge>
      </div>
    </div>
  </div>
);

export const AdminUsersTab: FC = () => {
  const { users, loading, error, refresh, invalidateSessions } =
    useAdminUsers();
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [orgFilter, setOrgFilter] = useState("");
  const [activeSessionsOnly, setActiveSessionsOnly] = useState(false);
  const [withoutOrganizationsOnly, setWithoutOrganizationsOnly] =
    useState(false);
  const [page, setPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [confirmUserId, setConfirmUserId] = useState<string | null>(null);
  const [invalidating, setInvalidating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const normalizedQuery = useMemo(() => query.trim().toLowerCase(), [query]);

  const orgFilterOptions = useMemo(() => {
    const orgs = new Map<string, string>();
    for (const user of users) {
      for (const org of user.organizations) {
        orgs.set(org.organization_id, org.name);
      }
    }
    return [
      { label: "All Organizations", value: "" },
      ...Array.from(orgs.entries())
        .toSorted(([, a], [, b]) => a.localeCompare(b))
        .map(([id, name]) => ({ label: name, value: id })),
    ];
  }, [users]);

  const filteredUsers = useMemo(
    () =>
      users
        .filter((user) => {
          if (
            normalizedQuery &&
            !user.email.toLowerCase().includes(normalizedQuery) &&
            !(user.name ?? "").toLowerCase().includes(normalizedQuery)
          ) {
            return false;
          }
          if (roleFilter && user.global_role !== roleFilter) {
            return false;
          }
          if (
            orgFilter &&
            !user.organizations.some((org) => org.organization_id === orgFilter)
          ) {
            return false;
          }
          if (activeSessionsOnly && user.active_sessions <= 0) {
            return false;
          }
          if (withoutOrganizationsOnly && user.organizations.length > 0) {
            return false;
          }
          return true;
        })
        .sort((a, b) => a.email.localeCompare(b.email)),
    [
      users,
      normalizedQuery,
      roleFilter,
      orgFilter,
      activeSessionsOnly,
      withoutOrganizationsOnly,
    ],
  );

  const pagedUsers = useMemo(
    () => filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredUsers, page],
  );

  useEffect(() => {
    setPage(1);
  }, [
    normalizedQuery,
    roleFilter,
    orgFilter,
    activeSessionsOnly,
    withoutOrganizationsOnly,
  ]);

  const confirmUser = useMemo(
    () => users.find((u) => u.user_id === confirmUserId) ?? null,
    [users, confirmUserId],
  );

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refresh();
    } finally {
      setRefreshing(false);
    }
  }, [refresh]);

  const handleConfirmInvalidate = useCallback(async () => {
    if (!confirmUserId) {
      return;
    }
    setInvalidating(true);
    setActionError(null);
    setActionSuccess(null);
    try {
      await invalidateSessions(confirmUserId);
      setActionSuccess(
        `Sessions invalidated for ${confirmUser?.email ?? confirmUserId}.`,
      );
    } catch (caughtError) {
      setActionError(
        caughtError instanceof Error
          ? caughtError.message
          : "Failed to invalidate sessions.",
      );
    } finally {
      setInvalidating(false);
      setConfirmUserId(null);
    }
  }, [confirmUserId, confirmUser, invalidateSessions]);

  const resetFilters = useCallback(() => {
    setActiveSessionsOnly(false);
    setOrgFilter("");
    setQuery("");
    setRoleFilter("");
    setWithoutOrganizationsOnly(false);
  }, []);

  const summary = useMemo(() => {
    const adminCount = users.filter(
      (user) => user.global_role === "admin",
    ).length;
    const withSessions = users.filter(
      (user) => user.active_sessions > 0,
    ).length;
    const withoutOrganizations = users.filter(
      (user) => user.organizations.length === 0,
    ).length;
    return { adminCount, withSessions, withoutOrganizations };
  }, [users]);

  const activeFilters = useMemo(() => {
    const filters = [];
    if (normalizedQuery) {
      filters.push({
        id: "query",
        label: `Search: ${query.trim()}`,
        onClear: () => setQuery(""),
      });
    }
    if (roleFilter) {
      filters.push({
        id: "role",
        label: `Role: ${roleFilter}`,
        onClear: () => setRoleFilter(""),
      });
    }
    if (orgFilter) {
      const orgLabel =
        orgFilterOptions.find((option) => option.value === orgFilter)?.label ??
        orgFilter;
      filters.push({
        id: "organization",
        label: `Org: ${orgLabel}`,
        onClear: () => setOrgFilter(""),
      });
    }
    if (activeSessionsOnly) {
      filters.push({
        id: "active-sessions",
        label: "Active sessions only",
        onClear: () => setActiveSessionsOnly(false),
      });
    }
    if (withoutOrganizationsOnly) {
      filters.push({
        id: "without-organizations",
        label: "Without organizations",
        onClear: () => setWithoutOrganizationsOnly(false),
      });
    }
    return filters;
  }, [
    activeSessionsOnly,
    normalizedQuery,
    orgFilter,
    orgFilterOptions,
    query,
    roleFilter,
    withoutOrganizationsOnly,
  ]);

  const metricCards = [
    {
      label: "Total Users",
      subValue: `${filteredUsers.length} visible now`,
      value: users.length,
      variant: "indigo",
    },
    {
      label: "Admins",
      subValue: `${users.length - summary.adminCount} standard users`,
      value: summary.adminCount,
      variant: "violet",
    },
    {
      label: "Active Sessions",
      subValue: activeSessionsOnly ? "filter enabled" : "can be invalidated",
      value: summary.withSessions,
      variant: "emerald",
    },
    {
      label: "Without Organizations",
      subValue: withoutOrganizationsOnly
        ? "filter enabled"
        : "membership gap check",
      value: summary.withoutOrganizations,
      variant: "amber",
    },
  ] as const;

  if (loading) {
    return <LoadingState message="Loading users…" />;
  }
  if (error) {
    return <Alert variant="error">{error}</Alert>;
  }

  return (
    <div className="space-y-4">
      <PanelSection
        badge={
          <Badge variant="info" size="sm">
            platform admin
          </Badge>
        }
        headerContent={
          <PanelHeaderBlock
            eyebrow="Identity"
            title="User Command Center"
            description="Review access posture, isolate risky slices, and invalidate active sessions with less list-scanning."
          />
        }
      >
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(320px,0.9fr)]">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {metricCards.map((card) => (
              <MetricCard
                key={card.label}
                label={card.label}
                value={card.value}
                variant={card.variant}
                size="lg"
                subValue={card.subValue}
              />
            ))}
          </div>

          <UserSessionSummary
            filteredCount={filteredUsers.length}
            totalCount={users.length}
            usersWithSessions={summary.withSessions}
            usersWithoutOrganizations={summary.withoutOrganizations}
          />
        </div>
      </PanelSection>

      {actionError && <Alert variant="error">{actionError}</Alert>}
      {actionSuccess && <Alert variant="success">{actionSuccess}</Alert>}

      <PanelSection
        badge={
          <Badge variant="default" size="sm">
            {filteredUsers.length} total
          </Badge>
        }
        headerContent={
          <PanelHeaderBlock
            eyebrow="Directory"
            title="Users"
            description="Search by identity, narrow by access scope, and focus on the accounts that still have live sessions."
          />
        }
      >
        <div className="mb-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-[minmax(220px,1fr)_160px_200px_auto_auto_auto]">
          <FormInput
            label="Search"
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder="Search by email or name"
            size="sm"
          />
          <FormSelect
            label="Role"
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.currentTarget.value)}
            options={ROLE_FILTER_OPTIONS}
            size="sm"
          />
          <FormSelect
            label="Organization"
            value={orgFilter}
            onChange={(event) => setOrgFilter(event.currentTarget.value)}
            options={orgFilterOptions}
            size="sm"
          />
          <div className="mt-5">
            <FormCheckbox
              label="Active sessions only"
              checked={activeSessionsOnly}
              onChange={setActiveSessionsOnly}
            />
          </div>
          <div className="mt-5">
            <FormCheckbox
              label="Without organizations"
              checked={withoutOrganizationsOnly}
              onChange={setWithoutOrganizationsOnly}
            />
          </div>
          <div className="mt-4 flex items-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleRefresh()}
              loading={refreshing}
              loadingText="Refreshing..."
            >
              Refresh
            </Button>
          </div>
        </div>

        <AdminActiveFiltersRow
          filters={activeFilters}
          onReset={resetFilters}
          totalLabel={`${filteredUsers.length} of ${users.length} visible`}
        />

        <DataTable responsive>
          <DataTable.Header>
            <tr>
              <DataTable.HeaderCell>Email</DataTable.HeaderCell>
              <DataTable.HeaderCell className="hidden sm:table-cell">
                Name
              </DataTable.HeaderCell>
              <DataTable.HeaderCell className="hidden md:table-cell">
                Role
              </DataTable.HeaderCell>
              <DataTable.HeaderCell className="hidden lg:table-cell">
                Organizations
              </DataTable.HeaderCell>
              <DataTable.HeaderCell className="hidden md:table-cell">
                Sessions
              </DataTable.HeaderCell>
              <DataTable.HeaderCell className="hidden xl:table-cell">
                Created
              </DataTable.HeaderCell>
              <DataTable.HeaderCell>Actions</DataTable.HeaderCell>
            </tr>
          </DataTable.Header>
          <DataTable.Body>
            {pagedUsers.length === 0 ? (
              <DataTable.Row>
                <DataTable.Cell
                  colSpan={7}
                  className="py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                >
                  No users match the current filters.
                </DataTable.Cell>
              </DataTable.Row>
            ) : (
              pagedUsers.map((user) => (
                <AdminUserRow
                  key={user.user_id}
                  user={user}
                  onInvalidate={setConfirmUserId}
                />
              ))
            )}
          </DataTable.Body>
        </DataTable>

        {filteredUsers.length > PAGE_SIZE ? (
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={filteredUsers.length}
            onPageChange={setPage}
          />
        ) : null}
      </PanelSection>

      <ConfirmationDialog
        isOpen={confirmUserId !== null}
        title="Invalidate Sessions"
        body={
          <>
            Are you sure you want to invalidate all active sessions for{" "}
            <strong>{confirmUser?.email}</strong>? They will be logged out
            immediately.
          </>
        }
        confirmLabel="Invalidate"
        onConfirm={() => void handleConfirmInvalidate()}
        onClose={() => setConfirmUserId(null)}
        isProcessing={invalidating}
      />
    </div>
  );
};
